import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HandCoins, AlertTriangle, CalendarClock, CheckCircle2, ReceiptText, Car } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api, { fmtNum, fmtDate } from "../lib/api";

const dHr = (n) => (n % 10 === 1 && n % 100 !== 11 ? "dan" : "dana");

export const dueBadge = (days) => {
  if (days > 0) return { cls: "bg-[#DC2626]/15 text-[#DC2626]", label: `Kasni ${days} ${dHr(days)}` };
  if (days === 0) return { cls: "bg-[#D97706]/15 text-[#D97706]", label: "Dospijeva danas" };
  return { cls: "bg-[#F1F5F9] text-[#475569]", label: `Dospijeva za ${-days} ${dHr(-days)}` };
};

export default function ReceivablesPage() {
  const [data, setData] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/receivables").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const markPaid = async () => {
    if (!confirmItem) return;
    setSaving(true);
    try {
      await api.post(`/transactions/${confirmItem.id}/realize`);
      toast.success(`Naplaćeno: ${confirmItem.counterparty || "račun"} ${fmtNum(confirmItem.amount, confirmItem.currency === "EUR" ? "€" : "KM")}`);
      setConfirmItem(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri označavanju");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <p className="text-sm text-[#475569]">Učitavanje...</p>;

  return (
    <div className="space-y-6 fade-up" data-testid="receivables-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Izlazni računi</p>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Nenaplaćeno</h1>
        <p className="text-sm text-[#475569] mt-2">Sve planirane naplate koje još čekaju uplatu kupca — datum je DVO (dospijeće). Naplata se automatski ostvaruje uvozom izvoda, a ovdje je možete označiti i ručno.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#0D9488] text-white rounded-sm p-5" data-testid="receivables-total-card">
          <div className="flex items-center gap-2 mb-3">
            <HandCoins className="w-4 h-4 opacity-70" />
            <p className="text-xs uppercase tracking-[0.15em] opacity-70">Ukupno nenaplaćeno</p>
          </div>
          <p className="font-heading text-3xl font-bold tabular-nums">{fmtNum(data.total_eur, "€")}</p>
          <p className="text-xs opacity-60 mt-1 tabular-nums">
            {data.count} {data.count === 1 ? "račun" : data.count < 5 ? "računa" : "računa"}
            {Object.entries(data.total_by_currency).map(([c, v]) => ` · ${fmtNum(v, c === "EUR" ? "€" : "KM")}`).join("")}
          </p>
        </div>
        <div className={`bg-white border rounded-sm p-5 ${data.overdue_count > 0 ? "border-[#DC2626]/40" : "border-[#E2E8F0]"}`} data-testid="receivables-overdue-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className={`w-4 h-4 ${data.overdue_count > 0 ? "text-[#DC2626]" : "text-[#64748B]"}`} />
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B]">Dospjelo — kasni</p>
          </div>
          <p className={`font-heading text-2xl font-bold tabular-nums ${data.overdue_count > 0 ? "text-[#DC2626]" : "text-[#0F172A]"}`}>{fmtNum(data.overdue_total_eur, "€")}</p>
          <p className="text-xs text-[#475569] mt-1">
            {data.overdue_count === 0 ? "Nijedan račun ne kasni." : `${data.overdue_count} ${data.overdue_count === 1 ? "račun kasni" : "računa kasni"} · najviše ${data.max_days_overdue} ${dHr(data.max_days_overdue)}`}
          </p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="receivables-upcoming-card">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-4 h-4 text-[#D97706]" />
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B]">U roku — čeka dospijeće</p>
          </div>
          <p className="font-heading text-2xl font-bold text-[#0F172A] tabular-nums">{fmtNum(data.total_eur - data.overdue_total_eur, "€")}</p>
          <p className="text-xs text-[#475569] mt-1">{data.count - data.overdue_count} {data.count - data.overdue_count === 1 ? "račun" : "računa"} još nije dospjelo</p>
        </div>
      </div>

      {data.items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#CBD5E1] rounded-sm p-10 text-center text-sm text-[#64748B]" data-testid="receivables-empty-state">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-[#2F7D4F]" />
          Sve je naplaćeno — trenutno nema izdanih računa koji čekaju uplatu.
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
          <Table data-testid="receivables-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">#</TableHead>
                <TableHead>DVO (dospijeće)</TableHead>
                <TableHead>Kupac</TableHead>
                <TableHead>Broj računa</TableHead>
                <TableHead>Banka</TableHead>
                <TableHead className="text-right">Iznos</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Akcija</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((r, i) => {
                const b = dueBadge(r.days_overdue);
                return (
                  <TableRow key={r.id} data-testid={`receivable-row-${i}`} className={r.days_overdue > 0 ? "bg-[#DC2626]/5" : ""}>
                    <TableCell className="text-xs text-[#64748B] tabular-nums">{i + 1}.</TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {fmtDate(r.date)}
                      {r.expected_date && r.expected_date !== r.date && (
                        <span data-testid={`receivable-expected-${i}`} className="block text-[11px] text-[#D97706] font-medium">očekivano {fmtDate(r.expected_date)}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-[#0F172A]">{r.counterparty || "—"}</span>
                      {r.vehicle && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-[#475569] bg-[#F1F5F9] rounded-sm px-1.5 py-0.5"><Car className="w-3 h-3" />{r.vehicle}</span>
                      )}
                      {r.description && <span className="block text-[11px] text-[#94A3B8] truncate max-w-[280px]">{r.description}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-[#475569]">
                      {r.invoice_number ? (
                        <span className="inline-flex items-center gap-1.5"><ReceiptText className="w-3.5 h-3.5 text-[#94A3B8]" />{r.invoice_number}</span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-[#475569]">{r.bank || "—"}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-[#0D9488] whitespace-nowrap">
                      +{fmtNum(r.amount, r.currency === "EUR" ? "€" : "KM")}
                      {r.currency === "BAM" && <span className="block text-[11px] font-normal text-[#94A3B8]">{fmtNum(r.amount_eur, "€")}</span>}
                    </TableCell>
                    <TableCell>
                      <Badge data-testid={`receivable-status-${i}`} className={`rounded-sm text-[11px] whitespace-nowrap hover:bg-inherit ${b.cls}`}>{b.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button data-testid={`receivable-paid-btn-${i}`} size="sm" variant="outline" onClick={() => setConfirmItem(r)} className="h-7 text-xs rounded-sm border-[#CBD5E1] hover:bg-[#0D9488] hover:text-white hover:border-[#0D9488]">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Označi naplaćeno
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!confirmItem} onOpenChange={(o) => !o && setConfirmItem(null)}>
        <AlertDialogContent data-testid="receivable-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Označiti kao naplaćeno?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmItem && (
                <>Račun {confirmItem.invoice_number ? `br. ${confirmItem.invoice_number} ` : ""}— {confirmItem.counterparty || "kupac"}, <span className="font-semibold tabular-nums">{fmtNum(confirmItem.amount, confirmItem.currency === "EUR" ? "€" : "KM")}</span> — bit će prebačen u ostvareno i ući će u stvarni saldo banke. Ako uplata dolazi kroz izvod, radije pustite da se upari automatski pri uvozu.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="receivable-confirm-cancel" className="rounded-sm">Odustani</AlertDialogCancel>
            <AlertDialogAction data-testid="receivable-confirm-ok" disabled={saving} onClick={markPaid} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
              Da, naplaćeno je
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
